import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-react'
import type { AnalyticsData } from '@/lib/api'
import { fmt } from './utils'

export function UnitEconomicsSection({
  unitEconomics,
  isLoading,
}: {
  unitEconomics: AnalyticsData['unitEconomics']
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardContent>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16 mb-1" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const ltvCacStatus = () => {
    if (unitEconomics.ltvCacRatio >= 3)
      return { label: 'Saudável', classes: 'bg-primary/10 text-primary' }
    if (unitEconomics.ltvCacRatio >= 1)
      return { label: 'Atenção', classes: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }
    return { label: 'Crítico', classes: 'bg-destructive/10 text-destructive' }
  }
  const status = ltvCacStatus()

  const cards = [
    {
      title: 'CAC Real',
      value: fmt(unitEconomics.cacReal),
      description: 'Custo por cliente indicado',
      tooltip: '(Desconto concedido + Cashback resgatado) / Clientes únicos indicados',
    },
    {
      title: 'LTV Médio',
      value: fmt(unitEconomics.ltvMedio),
      description: 'Receita por cliente indicado',
      tooltip: '(Receita 1ª compra + Recompras) / Clientes únicos indicados',
    },
    {
      title: 'LTV:CAC',
      value: `${unitEconomics.ltvCacRatio}x`,
      description:
        unitEconomics.ltvCacRatio >= 3
          ? 'Saudável (> 3x)'
          : unitEconomics.ltvCacRatio >= 1
            ? 'Atenção (1-3x)'
            : 'Crítico (< 1x)',
      tooltip: 'LTV Médio / CAC Real — quanto de receita cada R$1 investido gera',
      badgeLabel: status.label,
      badgeClasses: status.classes,
    },
    {
      title: 'Taxa de Aprovação',
      value: `${Math.round(unitEconomics.taxaAprovacao * 100)}%`,
      description: '% de indicações aprovadas',
      tooltip: 'Indicações aprovadas + pendentes / Total de indicações',
    },
    {
      title: 'Taxa de Fraude',
      value: `${Math.round(unitEconomics.taxaFraude * 100)}%`,
      description: '% de indicações rejeitadas',
      tooltip: 'Indicações rejeitadas / Total de indicações',
    },
    {
      title: 'Taxa de Ativação',
      value: `${Math.round(unitEconomics.taxaAtivacao * 100)}%`,
      description: 'Membros com indicação',
      tooltip: 'Membros com ≥1 indicação válida / Total de membros',
    },
  ]

  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardContent>
              <div className="flex items-center gap-1 mb-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {card.title}
                </p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3 w-3 text-muted-foreground/40 cursor-help"
                      aria-hidden="true"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-55 text-xs">
                    {card.tooltip}
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold font-display">{card.value}</span>
                {card.badgeLabel && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${card.badgeClasses}`}
                  >
                    {card.badgeLabel}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  )
}
